import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { previewPanModule } from "../../services/api";

export default function PanModuleImport({ value, onChange, accessToken, disabled = false }) {
  const input = useRef(null);
  const request = useRef(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => { request.current += 1; }, []);

  const selectFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // Allow retrying the same file after a failed upload.
    if (!file) return;
    const id = ++request.current;
    onChange(null);
    setError("");
    setBusy(false);
    if (!/\.pan$/i.test(file.name) || file.size === 0 || file.size > 256 * 1024) {
      setError("Choose a non-empty .pan file no larger than 256 KB.");
      return;
    }
    setBusy(true);
    try {
      const result = await previewPanModule(file, accessToken);
      if (id === request.current) onChange(result);
    } catch (err) {
      if (id === request.current) setError(err?.message || "The PAN file could not be imported.");
    } finally {
      if (id === request.current) setBusy(false);
    }
  };

  const chars = value?.characteristics;
  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Upload a mono- or polycrystalline silicon module exported from PVsyst.
        Its electrical model and dimensions will be filled in for you.
      </Typography>
      <Box>
        <input ref={input} type="file" accept=".pan" aria-label="PAN file" hidden
          disabled={disabled || busy} onChange={selectFile} />
        <Button variant="outlined" startIcon={<UploadFileIcon />} disabled={disabled || busy}
          onClick={() => input.current?.click()}>
          {busy ? "Reading PAN file…" : chars ? "Replace PAN file" : "Choose PAN file"}
        </Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {chars && (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography fontWeight={600}>{value.module_name}</Typography>
            <Chip size="small" label="PVsyst model" color="success" variant="outlined" />
          </Stack>
          <Typography variant="body2">{chars.Vendor} · {chars.STC} W · {chars.Bifacial ? "Bifacial" : "Monofacial"}</Typography>
          <Typography variant="body2">{chars.Length} × {chars.Width} m · {chars.cells_in_series} cells in series</Typography>
          <Typography variant="body2">Vmp {chars.V_mp_ref} V · Imp {chars.I_mp_ref} A · Voc {chars.V_oc_ref} V · Isc {chars.I_sc_ref} A</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, overflowWrap: "anywhere" }}>
            {chars.panSource.filename} · Calculated power at STC: {value.model_power_w.toFixed(2)} W
          </Typography>
        </Box>
      )}
      {(value?.warnings || []).map((warning) => <Alert key={warning} severity="warning">{warning}</Alert>)}
      <Typography variant="caption" color="text.secondary">
        This imports the module’s electrical model. Mounting, temperature, optical effects and system losses are configured in your project.
      </Typography>
    </Stack>
  );
}
